import type { DeepReadonly } from 'vue';

import type {
  OldPax,
  OldRoomPax,
  Pax,
  RoomPax,
} from '@libs/trip-planner-interfaces';

export class TripPlannerPax {
  private pax: TripPlannerRoomPax[] = [];

  constructor(pax: Pax) {
    this.rooms = pax;
  }

  public addRoom(): void {
    this.pax = [...this.pax, new TripPlannerRoomPax(1)];
  }

  public removeLastRoom(): void {
    if (this.pax.length === 1) {
      return;
    }

    this.pax = this.pax.slice(0, -1);
  }

  public get tripPlannerRooms(): TripPlannerRoomPax[] {
    return this.pax;
  }

  public get rooms(): RoomPax[] {
    return this.pax.map((roomPax) => ({ ...roomPax.value }));
  }

  public set rooms(pax: Pax) {
    this.pax = pax.map(
      (roomPax) =>
        new TripPlannerRoomPax(roomPax.numberOfAdults, roomPax.childAges)
    );
  }

  public get totalPax(): {
    adults: number;
    children: number;
  } {
    return this.pax.reduce(
      (total, room) => {
        total.adults += room.numberOfAdultsInRoom;
        total.children += room.numberOfChildrenInRoom;

        return total;
      },
      { adults: 0, children: 0 }
    );
  }

  public get totalNumberOfAdults(): number {
    return this.pax.reduce(
      (total, room) => (total += room.numberOfAdultsInRoom),
      0
    );
  }

  public get totalNumberOfChildren(): number {
    return this.pax.reduce(
      (total, room) => (total += room.numberOfChildrenInRoom),
      0
    );
  }

  public get totalNumberOfPax(): number {
    return this.totalNumberOfAdults + this.totalNumberOfChildren;
  }

  public get formattedChildrenAges(): string {
    const ellipsis = '...';
    const delimiter = ', ';
    const numberAgesToShow = 2;

    const totalChildrenAges = this.pax.flatMap((room) =>
      room.childAgesInRoom.map((age) => age.toString())
    );

    if (totalChildrenAges.length > numberAgesToShow) {
      totalChildrenAges.splice(numberAgesToShow);
      totalChildrenAges.push(ellipsis);
    }

    return totalChildrenAges.join(delimiter);
  }

  // Backwards Compatibility
  public static isOldPax(pax: OldPax | DeepReadonly<Pax>): pax is OldPax {
    return pax.some((roomPax: OldRoomPax | DeepReadonly<RoomPax>) =>
      Array.isArray(roomPax)
    );
  }

  // Backwards Compatibility
  public static getPaxFromOldPax(oldPax: OldPax): Pax {
    return oldPax.map<RoomPax>((oldRoomPax) => {
      const childAges = oldRoomPax.filter<number>(
        (age): age is number => age !== null
      );

      const numberOfAdults = oldRoomPax.length - childAges.length;

      return {
        numberOfAdults,
        childAges: childAges.length > 0 ? childAges : undefined,
      };
    });
  }
}

export class TripPlannerRoomPax implements RoomPax {
  constructor(
    public numberOfAdults = 2,
    public childAges?: number[]
  ) {}

  public addAdult(): void {
    this.numberOfAdults++;
  }

  public addChild(age = 0): void {
    this.childAges ? this.childAges.push(age) : (this.childAges = [age]);
  }

  public setChildAge(childIndex: number, age: number): void {
    if (this.childAges?.[childIndex] === undefined) {
      return;
    }
    this.childAges[childIndex] = age;
  }

  public removeLastAdult(): void {
    if (this.numberOfAdultsInRoom > 1) {
      this.numberOfAdults--;
    }
  }

  public removeLastChild(): void {
    this.childAges?.pop();
  }

  public get value(): RoomPax {
    return {
      numberOfAdults: this.numberOfAdults,
      childAges: this.childAges ? [...this.childAges] : undefined,
    };
  }

  public get childAgesInRoom(): number[] {
    return this.childAges ?? [];
  }

  public get numberOfAdultsInRoom(): number {
    return this.numberOfAdults;
  }

  public get numberOfChildrenInRoom(): number {
    return this.childAgesInRoom.length;
  }
}
